import { onBeforeUnmount, onMounted, reactive, ref, shallowRef, watch, type Ref } from 'vue'
import { exchangeApi, normalizeVacancy } from './exchangeApi'
import type { ExchangeSummary, ExchangeView, ListQuery, Vacancy } from './exchangeTypes'

export type ListStatus = 'loading' | 'ready' | 'error'
export type LoadMode = 'reset' | 'background' | 'action'

const BACKGROUND_INTERVAL = 5 * 60_000
const STALE_AFTER = 60_000

// Счётчики вкладок — из последнего успешного summary любого списка (одна агрегированная операция).
export const exchangeTabCounts = reactive<{ vacancies: number | null, requests: number | null }>({ vacancies: null, requests: null })

interface Options {
    // Вкладка видима: таймер и возврат обновляют только её.
    active: Ref<boolean>
    // Страница вышла за пределы после удаления/фильтра — перейти на последнюю.
    onPageOverflow?: (lastPage: number) => void
}

/**
 * Список биржи: серверные фильтры/порядок/пагинация + summary одной операцией.
 * Обновление: 5 минут на видимой вкладке, по возвращении при данных старше минуты, сразу после действий.
 * Одновременные обновления не дублируются, устаревшие ответы отбрасываются, ошибка фона данные не очищает.
 * Сбой summary (в т.ч. 404/405) не переключает страницу на локальные подсчёты: список остаётся,
 * счётчики не подменяются нулями, показывается «Не удалось обновить данные · Повторить».
 */
export function useExchangeList(view: ExchangeView, query: Ref<ListQuery>, options: Options) {
    const items = shallowRef<Vacancy[]>([])
    const count = ref(0)
    const status = ref<ListStatus>('loading')
    const backgroundError = ref(false)
    const summary = ref<ExchangeSummary | null>(null)
    const lastSuccess = ref(0)

    let seq = 0
    let inflight: Promise<void> | null = null
    let rerun = false
    let timer: ReturnType<typeof setInterval> | undefined

    async function run(mode: LoadMode) {
        const my = ++seq
        const snapshot = query.value
        if (mode === 'reset') {
            status.value = 'loading'
            backgroundError.value = false
        }
        const [list, sum] = await Promise.allSettled([exchangeApi.list(view, snapshot), exchangeApi.summary(view, snapshot)])
        if (my !== seq) return
        if (list.status === 'fulfilled') {
            const page = list.value
            if (!page.results.length && snapshot.page > 1 && page.count > 0) {
                options.onPageOverflow?.(Math.max(1, Math.ceil(page.count / snapshot.pageSize)))
            }
            items.value = page.results.map(normalizeVacancy)
            count.value = page.count
            status.value = 'ready'
            // Список получен, а счётчики нет — данные показаны, сбой счётчиков виден с «Повторить».
            backgroundError.value = sum.status === 'rejected'
            lastSuccess.value = Date.now()
        } else if (status.value === 'ready' && mode !== 'reset') {
            backgroundError.value = true
        } else {
            status.value = 'error'
        }
        if (sum.status === 'fulfilled') {
            summary.value = sum.value
            if (sum.value?.tabs) {
                exchangeTabCounts.vacancies = sum.value.tabs.vacancies_needing_decision ?? null
                exchangeTabCounts.requests = sum.value.tabs.incoming_requests_pending ?? null
            }
        } else if (mode === 'reset') {
            // Сводка другого запроса не показывается как текущая; прежняя при фоновом сбое остаётся.
            summary.value = null
        }
    }

    // Ждать, пока отработает последний запущенный запрос: если за время ожидания его сменил более новый
    // (действие/сброс), ждать и его. Так await load('action') завершается только на данных после mutation.
    async function settle(started: Promise<void>): Promise<void> {
        let current = started
        await current
        while (inflight && inflight !== current) {
            current = inflight
            await current
        }
    }

    function start(mode: LoadMode): Promise<void> {
        // run увеличивает seq: ответ уже идущего запроса (в т.ч. фонового, начатого до mutation) отбрасывается.
        const current = run(mode).finally(() => {
            if (inflight !== current) return
            inflight = null
            if (rerun) {
                rerun = false
                void load('background')
            }
        })
        inflight = current
        rerun = false
        return current
    }

    function load(mode: LoadMode = 'background'): Promise<void> {
        // Дедупликация только фоновых обновлений: они присоединяются к идущему и повторяются после него.
        if (mode === 'background' && inflight) {
            rerun = true
            return settle(inflight)
        }
        // После действия — всегда новый запрос, начатый после mutation; старый результат не применяется.
        return settle(start(mode))
    }

    function onVisibility() {
        if (document.visibilityState !== 'visible' || !options.active.value) return
        if (Date.now() - lastSuccess.value > STALE_AFTER) void load('background')
    }

    watch(() => JSON.stringify(query.value), () => { void load('reset') })

    watch(options.active, active => {
        if (active && status.value !== 'loading' && Date.now() - lastSuccess.value > STALE_AFTER) void load('background')
    })

    onMounted(() => {
        void load('reset')
        timer = setInterval(() => {
            if (options.active.value && document.visibilityState === 'visible') void load('background')
        }, BACKGROUND_INTERVAL)
        document.addEventListener('visibilitychange', onVisibility)
    })

    onBeforeUnmount(() => {
        seq++
        if (timer) clearInterval(timer)
        document.removeEventListener('visibilitychange', onVisibility)
    })

    return { items, count, status, backgroundError, summary, load }
}
